import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import history from '../../../history';
import AddToFolderModal from '../../../components/Modal/addToFolderModal';
import Header from '../../../components/Header';
import HeaderAndFooter from '../../../components/HeaderAndFooter';
import DocumentFolder from '../../../components/DocumentFolder';
import SafetyTrainingList from '../../../components/training/SafetyTrainingList';
import TypeSelection from '../../../components/TypeSelection';
import List from '../../../components/List';
import {
  fetchTrainingRepeatables,
  fetchTrainingScanOnDemands,
  fetchAllTrainings,
  fetchDocumentFoldersByType,
  updateTraining,
  updateTrainingScanOnDemand,
  updateTrainingRepeatable,
  updateDocument
} from '../../../api/v4';
import RepeatableList from '../../../components/RepeatableList';
import { getActiveFolderSelector } from '../../../selectors/documents';
import { getLoggedInUser } from '../../../selectors/users';
import { TRAINING_CATEGORY_DROPDOWN } from '../../../constants/constants';

export default function TrainingList() {
  const [allTrainings, setTrainings] = useState([]);
  const [folders, setFolders] = useState([]);
  const [trainingType, setTrainingType] = useState('Active');
  const [addToFolderModal, setFolderModal] = useState(false);
  const [documentFolderId, setFolderId] = useState('');
  const [addToFolder, setAddFolder] = useState([]);
  const [repeatingTrainings, setRepeatingTrainings] = useState([]);
  const [SODTrainings, setSOD] = useState([]);
  const activeFolder = useSelector(getActiveFolderSelector);
  const user = useSelector(getLoggedInUser);
  useEffect(() => {
    fetchAllTrainings(activeFolder._id).then(response => {
      setTrainings(response);
    });
    fetchDocumentFoldersByType('SafetyTraining').then(response =>
      setFolders(response)
    );
    fetchTrainingRepeatables(activeFolder._id).then(response =>
      setRepeatingTrainings(response)
    );
    fetchTrainingScanOnDemands(activeFolder._id).then(response =>
      setSOD(response)
    );
  }, [activeFolder._id]);

  const getTraining = schedule => {
    if (schedule === 'Active') return getImmediateTable();
    else if (schedule === 'Repeating') return getRepeatingTable();
    return getScanTable();
  };

  const handleAddMultipleToFolders = (trainingType = 'Active') => {
    let addDocsToFolder = [];

    let trainingList =
      trainingType === 'Active'
        ? allTrainings
        : trainingType === 'Repeating'
        ? repeatingTrainings
        : SODTrainings;

    trainingList.forEach(document => {
      if (addToFolder.indexOf(document._id) > -1) {
        addDocsToFolder.push(document);
      }
    });

    for (const doc of addDocsToFolder) {
      switch (trainingType) {
        case 'Active':
          updateTraining({
            ...doc,
            fromAddFolder: true,
            documentFolderId: documentFolderId
          });
          break;
        case 'Repeating':
          updateTrainingRepeatable({
            ...doc,
            type: 'trainings',
            documentFolderId: documentFolderId,
            fromAddFolder: true
          });
          break;
        case 'On Demand':
          updateTrainingScanOnDemand({
            ...doc,
            type: 'trainings',
            documentFolderId: documentFolderId,
            fromAddFolder: true
          });
          break;

        default:
          updateDocument({ ...doc, documentFolderId: documentFolderId });
          break;
      }
    }
    setFolderModal(false);
    setFolderId('');
    setAddFolder([]);

    fetchTrainingRepeatables().then(response =>
      setRepeatingTrainings(response)
    );
    fetchTrainingScanOnDemands().then(response => setSOD(response));
  };

  const getImmediateTable = () => {
    return (
      <SafetyTrainingList
        trainings={allTrainings}
        addToFolder={ids => {
          setAddFolder(ids);
          setFolderModal(true);
        }}
        saveKey="trainingList"
        accessLevel={user.accessLevel}
      />
    );
  };

  const getRepeatingTable = () => {
    const repeatingColumns = [
      { key: 'title', label: 'Training Title' },
      {
        key: 'category',
        label: 'Training Category',
        accessor: t =>
          TRAINING_CATEGORY_DROPDOWN.find(c => c.value === t?.category)?.label,
        enum: TRAINING_CATEGORY_DROPDOWN.map(c => c.label)
      },
      {
        key: 'assignedTo',
        label: 'Employees',
        datatype: 'users',
        accessor: r => (r.training.assignBy === 1 ? '' : r.assignedTo)
      },
      {
        key: 'assignByJobTitles',
        label: 'Job Title',
        accessor: r => r.training.assignByJobTitles?.join(', ')
      },
      { key: 'lastUpdatedAt', label: 'Last Updated', datatype: 'date' },
      {
        key: 'repeatingOn',
        label: 'Repeat On',
        datatype: 'dow',
        frequency: 'frequency'
      }
    ];

    return (
      <RepeatableList
        repeatables={repeatingTrainings}
        repeatingColumns={repeatingColumns}
        handleRowClick={handleViewEdit}
        saveKey="repeatingTrainingList"
        type="Safety Trainings"
        updateData={() =>
          fetchTrainingRepeatables().then(response =>
            setRepeatingTrainings(response)
          )
        }
        actions={[
          {
            label: 'Add to Folder',
            color: 'blue',
            onClick: ids => {
              setAddFolder(ids);
              setFolderModal(true);
            }
          }
        ]}
      />
    );
  };

  const getScanTable = () => {
    const scanColumns = [
      { key: 'title', label: 'Training Title' },
      { key: 'lastUpdatedAt', label: 'Last Updated', datatype: 'date' },
      {
        key: 'category',
        label: 'Training Category',
        accessor: t =>
          TRAINING_CATEGORY_DROPDOWN.find(c => c.value === t.category)?.label ??
          '',
        enum: TRAINING_CATEGORY_DROPDOWN.map(c => c.label)
      },
      {
        key: 'qrCodeUrl',
        label: 'QR Code',
        datatype: 'url',
        text: 'Download QR Code'
      }
    ];

    return (
      <List
        saveKey="scanTrainingList"
        data={SODTrainings}
        dataIsHash
        settings={scanColumns}
        rowClick={handleViewEdit}
        getRowId={r => r._id}
        actions={[
          {
            label: 'Add to Folder',
            color: 'blue',
            onClick: ids => {
              setAddFolder(ids);
              setFolderModal(true);
            }
          }
        ]}
      />
    );
  };

  const handleViewEdit = async template => {
    if (Object.keys(template).length === 0) {
      history.push(`/app/training/create/`);
      return;
    } else if (trainingType === 'Repeating') {
      history.push(`/app/training/view/${template._id}/repeating`);
      return;
    } else if (trainingType === 'On Demand') {
      history.push(`/app/training/view/${template._id}/sod`);
      return;
    }
    history.push(`/app/training/view/${template._id}`);
  };

  const header = (
    <Header
      section="Trainings"
      rightButtons={{
        text: 'Create Training',
        onClick: () => {
          localStorage.removeItem('trainingTemplate');
          handleViewEdit({});
        },
        color: 'blue'
      }}
    />
  );
  return (
    <HeaderAndFooter Header={header}>
      <TypeSelection
        selected={trainingType}
        selectedArray={['Active', 'Repeating', 'On Demand']}
        onClick={value => setTrainingType(value)}
      />
      <DocumentFolder content={allTrainings} docType="Safety Training" />
      {getTraining(trainingType)}
      <AddToFolderModal
        isOpen={addToFolderModal}
        folderId={documentFolderId}
        onRequestClose={() => setFolderModal(false)}
        submitActions={() => handleAddMultipleToFolders(trainingType)}
        folderOptions={folders.map(folder => {
          return {
            value: folder._id,
            label: folder.name
          };
        })}
        onChange={value => setFolderId(value)}
      />
    </HeaderAndFooter>
  );
}
